import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { controlHeightClass } from '@/shared/ui/core/styles';
import type { ControlSize } from '@/shared/ui/core/types';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ControlSize;
  loading?: boolean;
  fullWidth?: boolean;
  asChild?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

const variantClass: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-on-primary hover:bg-primary-hover disabled:bg-primary-disabled disabled:text-primary-disabled-fg',
  secondary:
    'bg-surface text-text border border-border-strong hover:bg-bg disabled:text-text-disabled disabled:border-border',
  ghost: 'bg-transparent text-primary hover:bg-primary-soft disabled:text-text-disabled',
  danger:
    'bg-danger text-on-primary hover:bg-[var(--color-danger-hover)] disabled:bg-danger-soft disabled:text-text-disabled',
  soft: 'bg-primary-soft text-primary border border-primary/30 hover:bg-primary hover:text-on-primary hover:border-transparent transition-all disabled:text-text-disabled disabled:border-transparent',
};

const paddingClass: Record<ControlSize, string> = {
  sm: 'px-3 text-caption',
  md: 'px-4 text-body',
  lg: 'px-5 text-body',
  touch: 'px-5 text-body',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    asChild = false,
    leftIcon,
    rightIcon,
    disabled,
    children,
    type = 'button',
    onClick,
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const classes = cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap select-none rounded-control font-semibold cursor-pointer',
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
    'disabled:cursor-not-allowed',
    isDisabled && asChild && 'pointer-events-none cursor-not-allowed opacity-60',
    controlHeightClass[size],
    paddingClass[size],
    variantClass[variant],
    fullWidth && 'w-full',
    className,
  );

  if (asChild) {
    return (
      <Slot
        ref={ref}
        className={classes}
        aria-disabled={isDisabled || undefined}
        data-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : undefined}
        onClick={
          isDisabled
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : onClick
        }
        {...props}
      >
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={classes}
      onClick={onClick}
      {...props}
    >
      {loading ? <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden /> : leftIcon}
      {children}
      {!loading ? rightIcon : null}
      {loading ? <span className="sr-only">Đang xử lý</span> : null}
    </button>
  );
});
